import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';

const Section = ({ id, title, children }) => (
  <section id={id} className="mb-10 scroll-mt-32">
    <h2 className="text-xl sm:text-2xl font-bold text-[#1e3a5f] mb-3">{title}</h2>
    <div className="text-slate-700 text-[15px] leading-relaxed space-y-3">{children}</div>
  </section>
);

const PrivacyPolicy = () => {
  useEffect(() => {
    document.title = 'Privacy Policy — Abney Low Nursing Home';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <main className="pt-32 pb-16" data-testid="privacy-policy-page">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#1e3a5f] hover:text-[#c9a227] font-semibold mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
          <h1 className="text-3xl sm:text-4xl font-bold text-[#1e3a5f] mb-2">Privacy Policy</h1>
          <p className="text-sm text-slate-500 mb-2">Last updated: 2 April 2026 · Policy reference: GDPR08</p>
          <p className="text-slate-700 mb-10 text-[15px] leading-relaxed">
            Abney Low Nursing Home ("Abney Low", "we", "our", "us") is committed to protecting your
            privacy. This policy explains what personal data we collect through our website
            (<span className="font-mono">abneylow.co.uk</span>), how we use it, the legal bases we rely on, and the rights you have
            under the UK GDPR and the Data Protection Act 2018.
          </p>

          <Section id="who-we-are" title="1. Who we are">
            <p>
              We are Abney Low Nursing Home, operating from 17 Queen's Road, Hoylake, Wirral, CH47 2AG,
              United Kingdom. Abney Low Nursing Home is newly operated by RSJB Nursing Homes
              <span className="italic"> (company registration number: pending — please contact us for details)</span>. We are the "data
              controller" of the personal data you provide via this website.
            </p>
            <p>
              Our Data Protection contact is: <a href="mailto:manager@abneylownursing.co.uk" className="text-[#1e3a5f] underline underline-offset-2 hover:text-[#c9a227] font-semibold">manager@abneylownursing.co.uk</a> · 0151 632 4504.
            </p>
          </Section>

          <Section id="data-we-collect" title="2. Personal data we collect">
            <p>When you use our website, we may collect the following categories of information:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li><strong>Information you give us</strong> — via our contact form, visit-booking form, brochure request, careers application or complaints form: name, email address, postal address, telephone number, the message you send us and (for careers applicants) your CV.</li>
              <li><strong>Information we collect automatically</strong> — technical data including your IP address, browser type and version, operating system, time zone setting, and information about your visit (pages visited, time on page, referrer URL).</li>
              <li><strong>Cookies</strong> — small text files placed on your device. See our <Link to="/cookies" className="text-[#1e3a5f] underline underline-offset-2 hover:text-[#c9a227] font-semibold">Cookies Policy</Link> for the full list.</li>
            </ul>
          </Section>

          <Section id="how-we-use" title="3. How we use your data">
            <ul className="list-disc pl-6 space-y-1.5">
              <li>To respond to enquiries and arrange visits, brochures or care assessments</li>
              <li>To assess job applications for open vacancies at Abney Low</li>
              <li>To investigate and respond to complaints or feedback</li>
              <li>To operate, maintain and improve the website (site analytics, error monitoring)</li>
              <li>To meet our legal, regulatory and safeguarding obligations (e.g. CQC, safeguarding, health & safety)</li>
              <li>To send occasional information about our services — only if you have opted in</li>
            </ul>
          </Section>

          <Section id="legal-bases" title="4. Legal bases we rely on">
            <p>Under the UK GDPR we must have a lawful basis for processing personal data. The bases we rely on are:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li><strong>Consent</strong> — for optional analytics cookies and any marketing emails.</li>
              <li><strong>Contract</strong> — where we need to correspond with you to arrange care or a visit you have requested.</li>
              <li><strong>Legal obligation</strong> — where we must retain records for CQC, safeguarding, HMRC or other regulators.</li>
              <li><strong>Legitimate interests</strong> — to improve the website, prevent fraud, ensure the security of our systems and process job applications.</li>
            </ul>
          </Section>

          <Section id="sharing" title="5. Who we share your data with">
            <p>We do not sell your personal data. We share it only with:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li><strong>Our email provider</strong> — Resend (resend.com) — to deliver enquiry, booking and application notifications to our management inbox.</li>
              <li><strong>Our analytics provider</strong> — PostHog (posthog.com) — <em>only if you consent to analytical cookies</em>.</li>
              <li><strong>Our hosting provider</strong> — Emergent — which stores the website, database and file uploads.</li>
              <li><strong>Regulators or legal authorities</strong> — where we are required to disclose by law (CQC, ICO, safeguarding authorities, HMRC, courts).</li>
              <li><strong>Prospective purchasers</strong> — if the business is sold or restructured.</li>
            </ul>
          </Section>

          <Section id="retention" title="6. How long we keep your data">
            <ul className="list-disc pl-6 space-y-1.5">
              <li><strong>Enquiries & marketing correspondence</strong> — up to 2 years after last contact.</li>
              <li><strong>Visit bookings & brochure requests</strong> — up to 2 years after the visit or request.</li>
              <li><strong>Job applications</strong> (unsuccessful) — 6 months, then securely deleted.</li>
              <li><strong>Complaints</strong> — 6 years, in line with the Records Management Code of Practice for Health and Social Care.</li>
              <li><strong>Technical / server logs</strong> — 90 days.</li>
            </ul>
            <p>Data relating to residents (once you become a resident of Abney Low) is retained under our internal records management policy and shared with you at admission.</p>
          </Section>

          <Section id="transfers" title="7. International transfers">
            <p>
              Where possible, personal data is stored within the UK or the European Economic Area (EEA).
              Where a processor we use (such as PostHog or Resend) is based in the United States, we rely
              on the UK/EU International Data Transfer Agreement and/or approved Standard Contractual
              Clauses to ensure your data is protected to UK GDPR standards.
            </p>
          </Section>

          <Section id="your-rights" title="8. Your rights">
            <p>Under the UK GDPR you have the following rights over your personal data:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li><strong>Access</strong> — request a copy of the personal data we hold about you.</li>
              <li><strong>Rectification</strong> — ask us to correct inaccurate or incomplete data.</li>
              <li><strong>Erasure ("right to be forgotten")</strong> — ask us to delete your data where there is no lawful reason for us to keep it.</li>
              <li><strong>Restriction</strong> — ask us to stop processing your data while a query is resolved.</li>
              <li><strong>Portability</strong> — ask us to transfer your data to another provider in a machine-readable format.</li>
              <li><strong>Object</strong> — object to us processing your data based on legitimate interests, and to withdraw consent for anything you consented to.</li>
            </ul>
            <p>
              To exercise any of these rights, email us at{' '}
              <a href="mailto:manager@abneylownursing.co.uk" className="text-[#1e3a5f] underline underline-offset-2 hover:text-[#c9a227] font-semibold">manager@abneylownursing.co.uk</a>{' '}
              or call 0151 632 4504. We will respond within 30 days (or explain why we need longer, up to a further 60 days for complex requests).
            </p>
          </Section>

          <Section id="complaints" title="9. How to complain">
            <p>
              If you are not satisfied with how we have handled your personal data, please tell us first so
              we can put it right. We will acknowledge your complaint within 14 days and respond in full
              within 30 days.
            </p>
            <p>
              You also have the right to complain to the UK regulator, the Information Commissioner's
              Office (ICO):
            </p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Website: <a href="https://ico.org.uk/" target="_blank" rel="noopener noreferrer" className="text-[#1e3a5f] underline underline-offset-2 hover:text-[#c9a227] font-semibold">https://ico.org.uk/</a></li>
              <li>Telephone: 0303 123 1113</li>
              <li>Post: Information Commissioner's Office, Wycliffe House, Water Lane, Wilmslow, Cheshire, SK9 5AF</li>
            </ul>
          </Section>

          <Section id="changes" title="10. Changes to this policy">
            <p>
              We review this policy at least annually. When we make significant changes we will update
              the "last updated" date at the top of this page and, where appropriate, we will notify
              you by email.
            </p>
          </Section>

          <div className="mt-12 p-5 bg-slate-50 border border-slate-200 rounded-2xl text-sm text-slate-700">
            <strong className="block text-[#1e3a5f] mb-1">Get in touch</strong>
            Data Protection contact:{' '}
            <a href="mailto:manager@abneylownursing.co.uk" className="text-[#1e3a5f] underline underline-offset-2 hover:text-[#c9a227] font-semibold">manager@abneylownursing.co.uk</a> · 0151 632 4504 · Abney Low Nursing Home, 17 Queen's Road, Hoylake, Wirral, CH47 2AG.
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
